import axios, { AxiosError } from "axios";
import { useAuthStore } from "@/store/auth";

/**
 * Axios instance for the Django API. In dev the Vite proxy forwards /api to
 * the backend; set VITE_API_BASE_URL at build time for other deployments.
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL
    ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
    : "/api/v1",
  headers: { "Content-Type": "application/json" },
  timeout: 20_000,
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function refreshAccess(): Promise<string | null> {
  const { refreshToken, setAccess, logout } = useAuthStore.getState();
  if (!refreshToken) return null;
  try {
    const res = await axios.post(`${apiClient.defaults.baseURL}/auth/refresh/`, {
      refresh: refreshToken,
    });
    setAccess(res.data.access);
    return res.data.access as string;
  } catch {
    logout();
    return null;
  }
}

// On 401, try one silent refresh, then retry; otherwise sign out.
apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (typeof error.config & { _retried?: boolean }) | undefined;
    const isAuthCall = original?.url?.includes("/auth/");
    if (error.response?.status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      refreshing ??= refreshAccess().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return apiClient(original);
      }
    }
    return Promise.reject(error);
  },
);

/** Flatten a DRF error payload into one readable string. */
export function errorMessage(err: unknown): string {
  const e = err as AxiosError<unknown>;
  const data = e?.response?.data;
  if (!data) return e?.message || "Something went wrong";
  const flat = (v: unknown): string[] => {
    if (typeof v === "string") return [v];
    if (Array.isArray(v)) return v.flatMap(flat);
    if (v && typeof v === "object")
      return Object.entries(v as Record<string, unknown>).flatMap(([k, x]) =>
        flat(x).map((m) => (["detail", "non_field_errors", "missing"].includes(k) ? m : `${k}: ${m}`)),
      );
    return [];
  };
  return flat(data).join(" · ") || "Something went wrong";
}
